import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Skeleton,
  Chip
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Psychology as AnalysisIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const AnalysisDisplay = ({ mainObject, isLoading }) => {
  const [copied, setCopied] = useState(false);
  const [displayText, setDisplayText] = useState('');
  
  // Typewriter effect
  useEffect(() => {
    if (mainObject && !isLoading) {
      let currentIndex = 0;
      setDisplayText('');
      
      const interval = setInterval(() => {
        if (currentIndex <= mainObject.length) {
          setDisplayText(mainObject.substring(0, currentIndex));
          currentIndex += 3; // Show 3 characters at a time for faster display
        } else {
          clearInterval(interval);
          setDisplayText(mainObject); // Ensure full text is shown
        }
      }, 10); // Reduced interval from 30ms to 10ms
      
      return () => clearInterval(interval);
    }
  }, [mainObject, isLoading]);
  
  const handleCopy = () => {
    if (mainObject) {
      navigator.clipboard.writeText(mainObject);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  if (!mainObject && !isLoading) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'primary.dark',
          position: 'relative',
          overflow: 'visible'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: 24,
            px: 2,
            py: 0.5,
            background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
            borderRadius: 2
          }}
        >
          <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
            AI Analysis
          </Typography>
        </Box>
        
        <CardContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <AnalysisIcon sx={{ color: 'primary.main', mt: 0.5 }} />
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Main Object Identified:
              </Typography>
              
              {isLoading ? (
                <>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                </>
              ) : (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: 'monospace',
                    color: 'primary.light',
                    minHeight: '2em'
                  }}
                >
                  {displayText}
                  {mainObject && displayText.length < mainObject.length && (
                    <span style={{ opacity: 0.5 }}>|</span>
                  )}
                </Typography>
              )}
            </Box>
            
            {!isLoading && mainObject && (
              <Tooltip title={copied ? 'Copied!' : 'Copy text'}>
                <IconButton
                  onClick={handleCopy}
                  size="small"
                  sx={{
                    color: copied ? 'success.main' : 'text.secondary',
                    transition: 'color 0.3s'
                  }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {!isLoading && mainObject && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Chip
                label="Claude Sonnet 4"
                size="small"
                sx={{
                  bgcolor: 'rgba(99, 102, 241, 0.2)',
                  color: 'primary.light',
                  fontSize: '0.75rem'
                }}
              />
              <Chip
                label="AWS Bedrock"
                size="small"
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.2)',
                  color: 'warning.light',
                  fontSize: '0.75rem'
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AnalysisDisplay;